import React from "react";
import { CalendarToday, Speed } from "@mui/icons-material";
import type { ChipProps } from "@mui/material";
import { Box, Chip } from "@mui/material";
import { LinkIcon } from "lucide-react";
import { Song } from "../../../Entities/SongModel";

interface SongMetadataChipsProps {
  song: Song;
}

const getOriginalWebsite = (url: string) => {
  if (!url) return "ChordMaster";
  if (url.includes("tab4u")) return "Tab4U";
  if (url.includes("ultimate-guitar")) return "Ultimate Guitar";
  return "Original Source";
};

const getColor = (level: string | undefined): ChipProps["color"] => {
  if (!level) return "default";
  switch (level) {
    case "beginner":
      return "success";
    case "intermediate":
      return "warning";
    case "advanced":
    case "expert":
      return "error";
    default:
      return "default";
  }
};

export const SongMetadataChips: React.FC<SongMetadataChipsProps> = ({ song }) => {
  const hasMetadata = Boolean(
    song.difficulty ||
    song.tempo ||
    song.url ||
    song.created_at ||
    (song.tags && song.tags.length > 0)
  );

  if (!hasMetadata) return null;

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={0.75}
      alignItems="center"
      mb={1.5}
    >
      {song.difficulty && (
        <Chip
          label={`Difficulty: ${song.difficulty}`}
          color={getColor(song.difficulty)}
          variant="outlined"
          sx={{ px: 0.5 }}
        />
      )}
      {song.tempo && (
        <Chip
          icon={<Speed />}
          label={`${song.tempo} BPM`}
          variant="outlined"
          sx={{ px: 0.5 }}
        />
      )}
      {song.url && (
        <a
          href={song.url}
          target="_blank"
          rel="noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Chip
            color="info"
            icon={<LinkIcon size={14} />}
            label={getOriginalWebsite(song.url)}
            clickable
            sx={{ px: 0.5 }}
          />
        </a>
      )}
      {song.created_at && (
        <Chip
          icon={<CalendarToday />}
          label={`Added: ${new Date(song.created_at).toLocaleDateString()}`}
          variant="outlined"
          sx={{ px: 0.5 }}
        />
      )}
      {song.tags &&
        song.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            variant="outlined"
            sx={{ bgcolor: "#f8fafc", px: 0.5 }}
          />
        ))}
    </Box>
  );
};
